import { queryOptions, useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute } from "@tanstack/react-router"
import { format } from "date-fns"
import { ptBR } from "date-fns/locale"
import { Users } from "lucide-react"

import { ChangelogTabs } from "@/components/changelog/changelog-tabs"
import { Badge } from "@/components/ui/badge"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import { listSubscribers } from "@/http/changelog/subscribers"
import type { TChangelogSubscriber } from "@/http/changelog/types"

const subscribersQueryOptions = (params: {
  serviceId: string
  organizationSlug: string
}) =>
  queryOptions({
    queryKey: [
      "changelog-subscribers",
      params.serviceId,
      params.organizationSlug,
    ],
    queryFn: async () => {
      const response = await listSubscribers(params)
      if (response.error) {
        throw new Error(response.error.message)
      }
      return response.data
    },
  })

export const Route = createFileRoute(
  "/_authenticated/$orgSlug/changelog/services/$serviceId/subscribers",
)({
  loader: async ({ context, params }) => {
    await context.queryClient.ensureQueryData(
      subscribersQueryOptions({
        serviceId: params.serviceId,
        organizationSlug: params.orgSlug,
      }),
    )
    return {
      title: "Inscritos",
      path: `/${params.orgSlug}/changelog/services/${params.serviceId}/subscribers`,
    }
  },
  component: SubscribersPage,
})

function formatDate(dateString: string | null) {
  if (!dateString) return "-"
  return format(new Date(dateString), "dd/MM/yyyy HH:mm", {
    locale: ptBR,
  })
}

type TSubscriberStatus = "confirmed" | "pending" | "cancelled"

function getSubscriberStatus(
  subscriber: TChangelogSubscriber,
): TSubscriberStatus {
  if (subscriber.unsubscribedAt !== null) return "cancelled"
  if (subscriber.confirmedAt !== null) return "confirmed"
  return "pending"
}

function StatusBadge({ status }: { status: TSubscriberStatus }) {
  switch (status) {
    case "confirmed":
      return <Badge variant="default">Confirmado</Badge>
    case "pending":
      return <Badge variant="outline">Pendente</Badge>
    case "cancelled":
      return <Badge variant="destructive">Cancelado</Badge>
  }
}

function SubscribersPage() {
  const { orgSlug, serviceId } = Route.useParams()

  const { data } = useSuspenseQuery(
    subscribersQueryOptions({
      serviceId,
      organizationSlug: orgSlug,
    }),
  )

  const subscribers = data.subscribers
  const total = data.total

  const confirmed = subscribers.filter(
    (s) => s.confirmedAt !== null && s.unsubscribedAt === null,
  ).length

  const pending = subscribers.filter(
    (s) => s.confirmedAt === null && s.unsubscribedAt === null,
  ).length

  return (
    <div className="space-y-6">
      <ChangelogTabs
        orgSlug={orgSlug}
        serviceId={serviceId}
        activeTab="subscribers"
      />
      <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
        <div>
          <div className="flex items-center gap-2">
            <Users className="h-6 w-6" />
            <h1 className="text-2xl font-bold md:text-3xl">Inscritos</h1>
          </div>
          <p className="text-muted-foreground mt-2">
            Gerencie os inscritos do changelog.
          </p>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Total</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{total}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Confirmados</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{confirmed}</p>
          </CardContent>
        </Card>
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium">Pendentes</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-2xl font-bold">{pending}</p>
          </CardContent>
        </Card>
      </div>

      {subscribers.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-12">
            <Users className="text-muted-foreground mb-4 h-12 w-12" />
            <p className="text-muted-foreground text-lg">
              Nenhum subscriber encontrado.
            </p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Email</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Data de inscrição</TableHead>
                  <TableHead>Data de confirmação</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {subscribers.map((subscriber) => {
                  const status = getSubscriberStatus(subscriber)
                  return (
                    <TableRow key={subscriber.id}>
                      <TableCell className="font-medium">
                        {subscriber.email}
                      </TableCell>
                      <TableCell>
                        <StatusBadge status={status} />
                      </TableCell>
                      <TableCell>{formatDate(subscriber.createdAt)}</TableCell>
                      <TableCell>
                        {formatDate(subscriber.confirmedAt)}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  )
}
